window.Customization3DLoader = (function () {
  'use strict';

  const VENDOR_ORIGIN = 'https://spaces.miradoroutdoor.com';
  const SDK_FILE_API_DEFAULT = `${VENDOR_ORIGIN}/api/sdk/file?id=d90ce304-a1a6-4671-81fa-ad453b7ff903`;
  const BIZ_FILE_API_DEFAULT = `${VENDOR_ORIGIN}/api/sdk/file?id=7de609fb-0b62-449a-8dea-7a73b4476dd6`;
  const LEGACY_SDK_DEFAULT = `${VENDOR_ORIGIN}/sdk/index.global.js?v=123456`;

  let loadPromise = null;
  let warmupStarted = false;
  const resolvedFileUrlCache = new Map();
  const resolveFileUrlInflight = new Map();

  const trim = (v) => String(v || '').trim();

  const isLegacyMode = () => trim(window.CUSTOMIZATION_3D_BUSINESS_MODE).toLowerCase() === 'legacy';

  const getSdkFileApi = () => trim(window.CUSTOMIZATION_3D_SDK_FILE_API) || SDK_FILE_API_DEFAULT;
  const getBizFileApi = () => trim(window.CUSTOMIZATION_3D_BIZ_FILE_API) || BIZ_FILE_API_DEFAULT;
  const getLegacySdkUrl = () => trim(window.CUSTOMIZATION_3D_SDK_URL) || LEGACY_SDK_DEFAULT;

  const looksLikeFileApi = (url) => /\/api\/sdk\/file(\?|$)/i.test(url);
  const looksLikeScriptUrl = (url) => /\.(js|mjs|cjs)(\?|$)/i.test(url);

  const scriptAlreadyPresent = (src) => {
    if (!src) return false;
    return !!document.querySelector(`script[src="${src}"], script[data-c3d-resolved-src="${src}"]`);
  };

  const injectScript = (src, marker) => new Promise((resolve, reject) => {
    if (!src) {
      reject(new Error('[3D loader] empty script url'));
      return;
    }
    if (scriptAlreadyPresent(src) && window.faceSDK?.kernel && (isLegacyMode() || window.kernel_bl)) {
      resolve(src);
      return;
    }
    const s = document.createElement('script');
    s.src = src;
    s.async = false;
    s.dataset.c3dSdk = marker || '1';
    s.dataset.c3dResolvedSrc = src;
    s.onload = () => resolve(src);
    s.onerror = () => reject(new Error('[3D loader] script load failed: ' + src));
    document.body.appendChild(s);
  });

  const resolveFileUrl = async (apiUrl) => {
    const url = trim(apiUrl);
    if (!url) throw new Error('[3D loader] missing file API url');
    if (resolvedFileUrlCache.has(url)) return resolvedFileUrlCache.get(url);
    if (!looksLikeFileApi(url) && looksLikeScriptUrl(url)) {
      resolvedFileUrlCache.set(url, url);
      return url;
    }
    if (resolveFileUrlInflight.has(url)) return resolveFileUrlInflight.get(url);

    const pending = (async () => {
      const res = await fetch(url, { cache: 'no-store', credentials: 'omit' });
      if (!res.ok) throw new Error('[3D loader] file API HTTP ' + res.status + ': ' + url);
      const json = await res.json();
      if (Number(json?.code) !== 200 || !json?.data?.url) {
        throw new Error('[3D loader] file API did not return data.url: ' + url);
      }
      const fileUrl = trim(json.data.url);
      const version = trim(json.data.version);
      if (version && fileUrl && !/[?&]v=/.test(fileUrl)) {
        return fileUrl + (fileUrl.includes('?') ? '&' : '?') + 'v=' + encodeURIComponent(version);
      }
      return fileUrl;
    })();

    resolveFileUrlInflight.set(url, pending);
    try {
      const resolved = await pending;
      resolvedFileUrlCache.set(url, resolved);
      return resolved;
    } finally {
      resolveFileUrlInflight.delete(url);
    }
  };

  const warmupResolvers = () => {
    if (warmupStarted) return;
    warmupStarted = true;
    if (isLegacyMode()) {
      const href = getLegacySdkUrl();
      if (!href || document.querySelector(`link[rel="preload"][href="${href}"]`)) return;
      const link = document.createElement('link');
      link.rel = 'preload';
      link.as = 'script';
      link.href = href;
      document.head.appendChild(link);
      return;
    }
    [getSdkFileApi(), getBizFileApi()].forEach((apiUrl) => {
      resolveFileUrl(apiUrl).then((src) => {
        if (!src || document.querySelector(`link[rel="preload"][href="${src}"]`)) return;
        const link = document.createElement('link');
        link.rel = 'preload';
        link.as = 'script';
        link.href = src;
        document.head.appendChild(link);
      }).catch((err) => {
        console.warn('[3D loader] warmup failed', err);
      });
    });
  };

  const ensureLoaded = () => {
    if (loadPromise) return loadPromise;
    loadPromise = (async () => {
      try {
        if (isLegacyMode()) {
          const src = getLegacySdkUrl();
          if (!window.faceSDK?.kernel) await injectScript(src, 'legacy');
          if (!window.faceSDK?.kernel) throw new Error('[3D loader] faceSDK.kernel missing after legacy SDK load');
          return { mode: 'legacy', kernel: window.faceSDK.kernel, biz: null };
        }
        if (!window.faceSDK?.kernel) {
          const sdkSrc = await resolveFileUrl(getSdkFileApi());
          await injectScript(sdkSrc, 'sdk');
        }
        if (!window.faceSDK?.kernel) throw new Error('[3D loader] faceSDK.kernel missing after SDK load');
        if (!window.kernel_bl) {
          const bizSrc = await resolveFileUrl(getBizFileApi());
          await injectScript(bizSrc, 'biz');
        }
        if (!window.kernel_bl) throw new Error('[3D loader] kernel_bl missing after business layer load');
        return {
          mode: 'kernel_bl',
          kernel: window.faceSDK.kernel,
          biz: window.kernel_bl,
        };
      } catch (err) {
        loadPromise = null;
        throw err;
      }
    })();
    return loadPromise;
  };

  return {
    isLegacyMode,
    warmup: warmupResolvers,
    ensureLoaded,
    getSdkFileApi,
    getBizFileApi,
  };
})();
